/**
 * useExportActions — state + side-effects for <ExportMenu>.
 *
 * Owns:
 *   - dialog open flags (email + upsell)
 *   - email form state
 *   - busy flags (pdf build, email send)
 *   - all the per-format handlers (print/csv/xlsx/pdf/gsheets/email)
 *   - the Steel+ gating decision
 *
 * Returns a flat object of values + callbacks so the view layer is purely
 * declarative. Keeps the dialog/dropdown components free of business logic.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  csvBlob, xlsxBlob, screenPdfBlob,
  downloadCSV, downloadXLSX, downloadScreenPDF,
  blobToBase64,
} from '../../lib/exporters';
import { useAuth } from '../../contexts/AuthContext';
import { FORMATS } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useExportActions({ getData, pageTitle }) {
  const { user } = useAuth();
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', format: 'pdf', note: '' });
  const [emailing, setEmailing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);

  // Steel+ check — admin/staff/owner roles are always allowed (they need
  // export emails as part of their job), even on free tier.
  const isPro = user?.role === 'admin'
    || user?.role === 'staff'
    || user?.role === 'owner'
    || user?.tier === 'pro'
    || user?.tier === 'pro_plus';

  // Pull a row-shaped snapshot from the host page. Returns null + a toast
  // if there's nothing to export, so every handler can `if (!data) return`.
  const snapshot = () => {
    try {
      const data = getData();
      if (!data || !data.columns || !data.rows) {
        toast.error('Nothing to export yet');
        return null;
      }
      if (data.rows.length === 0) {
        toast.error('No data to export');
        return null;
      }
      return { ...data, title: pageTitle || data.sheetName || 'Forge Ledger export' };
    } catch {
      toast.error('Could not prepare export');
      return null;
    }
  };

  const handlePrint = () => window.print();

  const handleCSV = () => {
    const data = snapshot();
    if (!data) return;
    downloadCSV(data.columns, data.rows, data.fileName, data.title);
    toast.success('CSV downloaded');
  };

  const handleXLSX = () => {
    const data = snapshot();
    if (!data) return;
    downloadXLSX(data.columns, data.rows, data.fileName, data.sheetName, data.title);
    toast.success('Excel workbook downloaded');
  };

  const handlePDF = async () => {
    const data = snapshot();
    if (!data) return;
    setPdfBusy(true);
    try {
      await downloadScreenPDF(data.fileName, data.title);
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF export failed');
    } finally {
      setPdfBusy(false);
    }
  };

  const handleGoogleSheets = () => {
    const data = snapshot();
    if (!data) return;
    downloadCSV(data.columns, data.rows, data.fileName, data.title);
    toast.success('CSV downloaded — in Google Sheets, choose File → Import to open it.', { duration: 6000 });
  };

  const openEmailDialog = () => {
    if (!isPro) {
      setUpsellOpen(true);
      return;
    }
    setEmailForm({ to: user?.email || '', format: 'pdf', note: '' });
    setEmailOpen(true);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    const data = snapshot();
    if (!data) return;
    setEmailing(true);
    try {
      const fmt = FORMATS.find((f) => f.value === emailForm.format);
      const blob = await buildBlobForFormat(emailForm.format, data);
      const base64 = await blobToBase64(blob);
      const filename = `${data.fileName}.${fmt.ext}`;
      await axios.post(`${API}/export/email`, {
        to: emailForm.to.trim(),
        page_title: data.title,
        note: emailForm.note,
        filename,
        content_type: fmt.mime,
        content_base64: base64,
      }, { withCredentials: true });
      toast.success(`Sent to ${emailForm.to.trim()}`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email failed');
    } finally {
      setEmailing(false);
    }
  };

  return {
    // flags
    isPro,
    pdfBusy,
    emailing,
    // dialog state
    emailOpen, setEmailOpen,
    upsellOpen, setUpsellOpen,
    emailForm, setEmailForm,
    // actions
    handlePrint, handleCSV, handleXLSX, handlePDF, handleGoogleSheets,
    openEmailDialog, submitEmail,
  };
}

// Small helper kept private to the hook — chooses the right blob builder.
async function buildBlobForFormat(format, data) {
  switch (format) {
    case 'csv':  return csvBlob(data.columns, data.rows, { title: data.title });
    case 'xlsx': return xlsxBlob(data.columns, data.rows, data.sheetName, data.title);
    case 'pdf':  return screenPdfBlob(data.title);
    default: throw new Error('Unknown format');
  }
}
